import { ref, onBeforeUnmount } from 'vue';
import { requests } from '@/mixins/requests';

// eslint-disable-next-line import/prefer-default-export
export function useCollaboratorSearch () {
  const items = ref([]);
  const isLoading = ref(false);
  const hasSearched = ref(false);
  const timeout = ref(null);

  const clearSearch = () => {
    items.value = [];
    hasSearched.value = false;
    if (timeout.value) clearTimeout(timeout.value);
  };

  const searchCollaborators = async (query, options = {}) => {
    const {
      size = 1000,
      excludeEmails = [] // array of strings (emails)
    } = options;

    // Harmonized validation: ensure we have something to search
    if (!query || query.length === 0) {
      items.value = [];
      // Note: we don't necessarily reset hasSearched here depending on UX intent,
      // but commonly empty query means "not searching anymore" or cleared.
      // FindCandidate clears items but didn't explicitly set hasSearched=false in handleSearch, only in clearSearch.
      return;
    }

    try {
      isLoading.value = true;
      // hasSearched is true once we start looking
      // (Used for UI states like "No results found" vs "Start searching")

      const variables = {
        wtype: 'collaborator_profile',
        indice: 'ES_COLLABORATOR_INDICE',
        query: query,
        from: 0,
        size: size
      };

      const response = await requests.searchOpenSearchContent(variables);

      if (!response.success) {
        items.value = [];
        return;
      }

      let results = response.items || [];

      if (excludeEmails.length > 0) {
        const selectedSet = new Set(excludeEmails);
        results = results.filter(item => !selectedSet.has(item.email));
      }

      items.value = results;
    } catch (error) {
      console.error('[ERROR] Fetching collaborator => ', error);
      items.value = [];
    } finally {
      isLoading.value = false;
    }
  };

  const debouncedSearch = (query, options = {}) => {
    if (timeout.value) clearTimeout(timeout.value);

    // Immediate feedback state
    hasSearched.value = true;

    // Check min length if desired, otherwise just debounce
    // Popup had < 2 check. FindCandidate didn't.
    // We'll let searchCollaborators handle empty checks, but optional min length check could be here.

    timeout.value = setTimeout(() => {
      searchCollaborators(query, options);
    }, 500);
  };

  onBeforeUnmount(() => {
    if (timeout.value) {
      clearTimeout(timeout.value);
    }
  });

  return {
    items,
    isLoading,
    hasSearched,
    searchCollaborators,
    debouncedSearch,
    clearSearch
  };
}
